import './_typedef';
import { type SWRConfiguration } from 'swr';
import FeedContentType from '@/constants/FeedContentType';
import { CAMPAIGN_MAIN_TYPE } from '@/lib/constants/common';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import translate from '@/lib/intl/translate';
import type Campaign from '@/types/api/Campaign';
import showErrorMessage from '@/utils/toasts/showErrorMessage';
import showSuccessMessage from '@/utils/toasts/showSuccessMessage';
import useEntity from './useEntity';

/**
 * Fetches data for a campaign by ID.
 * @param {string?} campaignId
 * @param {object?} swrOptions
 * @returns {SwrEntity}
 */

const useCampaign = (campaignId: string, swrOptions?: SWRConfiguration) => {
  const ownerOrgId = useOwnerOrgId();
  const {
    data: entityData,
    mutate,
    remove,
    ...rest
  } = useEntity(campaignId, FeedContentType.CAMPAIGN, swrOptions);

  // TODO(jon): We should update `useEntity` to use typescript generics so we
  // can pass the desired type like `useEntity<Campaign>`, at which point we
  // would be able to remove this `as` defintiion.
  const data = entityData as Campaign | undefined;

  const deleteCampaign = async (showConfirmation = true) => {
    try {
      await remove();
      if (showConfirmation) {
        showSuccessMessage({
          message: translate('CAMPAIGN_DELETE_SUCCESS'),
        });
      }
    } catch (err) {
      if (showConfirmation) {
        showErrorMessage({
          message: translate('CAMPAIGN_DELETE_FAILURE'),
        });
      } else {
        throw err;
      }
    }
  };

  const mutateCampaign = async (
    newData: Partial<Campaign>,
    showConfirmation = true,
  ) => {
    if (!data) return;
    const { campaignType = CAMPAIGN_MAIN_TYPE } = data;

    try {
      await mutate({ ...newData, campaignId, campaignType, ownerOrgId });

      if (showConfirmation) {
        showSuccessMessage({ message: translate('CAMPAIGN_UPDATE_SUCCESS') });
      }
    } catch (error) {
      showErrorMessage({ message: translate('CAMPAIGN_UPDATE_FAILURE') });
    }
  };

  return {
    data,
    remove: deleteCampaign,
    mutate: mutateCampaign,
    ...rest,
  };
};

export default useCampaign;
